import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  Bot,
  Check,
  Clock,
  Copy,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  Key,
  KeyRound,
  Plus,
  RotateCcw,
  Search,
  SendHorizontal,
  ShieldOff,
  Sparkles,
  Trash2,
  Wallet,
  WifiOff,
  X,
  ZapOff,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { apiDelete, apiGet, apiPost, apiPut } from "../../utils/api";
import { getRole } from "../../utils/authService";
import "./visun-ai.css";

const KEY_MANAGER_ROLES = ["admin", "client", "ceo", "cfo"];

const MAX_PROMPT_LENGTH   = 50000;

const LOADING_MESSAGES = [
  "Analyzing activity data...",
  "Generating insights...",
  "Checking productivity patterns...",
  "Scanning recent alerts...",
  "Processing team metrics...",
];

const QUICK_PROMPTS = [
  { label: "Today's productivity summary",  prompt: "Show today's productivity summary for my team." },
  { label: "Why was productivity low?",      prompt: "Why was productivity low yesterday? What were the main causes?" },
  { label: "Top idle users today",           prompt: "Who are the top idle users today and for how long?" },
  { label: "Weekly report insights",         prompt: "Give me weekly report insights with key highlights and anomalies." },
  { label: "Apps wasting time",              prompt: "Which apps are wasting the most time across the team this week?" },
];

const ERROR_CONFIG = {
  rate_limit_minute:    { icon: Clock,         color: "warning", title: "Slow down"              },
  rate_limit_daily:     { icon: Clock,         color: "warning", title: "Daily limit reached"    },
  daily_cap:            { icon: AlertTriangle, color: "warning", title: "Daily spend cap reached" },
  insufficient_credits: { icon: Wallet,        color: "credits", title: "Not enough credits"     },
  no_ledger:            { icon: Wallet,        color: "credits", title: "No credit balance"      },
  blocked:              { icon: ShieldOff,     color: "danger",  title: "Account suspended"      },
  kill_switch:          { icon: ZapOff,        color: "muted",   title: "AI temporarily offline" },
  ai_key_required:      { icon: KeyRound,      color: "warning", title: "AI key required"        },
  ai_key_invalid:       { icon: KeyRound,      color: "danger",  title: "AI key rejected"         },
};

const newMessage = (role, content, meta = {}) => ({
  id:        `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  role,
  content,
  timestamp: new Date().toISOString(),
  ...meta,
});

const formatTime = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

function detectActions(content = "") {
  if (!content) return [];
  const t = content.toLowerCase();
  const out = [];
  if (/\b(dashboard|monitor|panel|overview|live data)\b/.test(t))            out.push("dashboard");
  if (/\b(report|export|download|pdf|csv|weekly|monthly summary)\b/.test(t)) out.push("report");
  if (/\b(investigate|suspicious|anomal|unusual activity|violation)\b/.test(t)) out.push("investigate");
  return [...new Set(out)];
}

export default function VisunAi() {
  const [messages,      setMessages]      = useState([]);
  const [draft,         setDraft]         = useState("");
  const [isSending,     setIsSending]     = useState(false);
  const [errorMessage,  setErrorMessage]  = useState("");
  const [errorCode,     setErrorCode]     = useState("");
  const [copiedId,      setCopiedId]      = useState("");
  const [isOnline,      setIsOnline]      = useState(
    () => typeof navigator === "undefined" ? true : navigator.onLine
  );
  const [rateLimitSecs, setRateLimitSecs] = useState(0);
  const [loadingMsgIdx, setLoadingMsgIdx] = useState(0);

  // Credit wallet state
  const [creditsUsd,   setCreditsUsd]   = useState(null);
  const [isLowCredits, setIsLowCredits] = useState(false);
  const [hasNoCredits, setHasNoCredits] = useState(false);

  // BYOK AI key state
  const role            = useMemo(() => getRole(), []);
  const canManageKey    = KEY_MANAGER_ROLES.includes(role);
  const [keyConfig,     setKeyConfig]     = useState(null); // null = not loaded yet
  const [showKeyPanel,  setShowKeyPanel]  = useState(false);
  const [keyProvider,   setKeyProvider]   = useState("gemini");
  const [keyApiKey,     setKeyApiKey]     = useState("");
  const [keyModel,      setKeyModel]      = useState("");
  const [showKeyValue,  setShowKeyValue]  = useState(false);
  const [keySaving,     setKeySaving]     = useState(false);
  const [keyDeleting,   setKeyDeleting]   = useState(false);
  const [keyPanelError, setKeyPanelError] = useState("");

  const msgListRef = useRef(null);
  const inputRef   = useRef(null);

  const fetchKeyConfig = useCallback(async () => {
    try {
      const res  = await apiGet("/api/visun-ai/key");
      const data = await res.json().catch(() => null);
      if (data?.success) {
        setKeyConfig(data);
        setKeyProvider(data.provider || "gemini");
        setKeyModel(data.model || "");
      }
    } catch { /* informational only */ }
  }, []);

  useEffect(() => { void fetchKeyConfig(); }, [fetchKeyConfig]);

  const openKeyPanel = useCallback(() => {
    setKeyPanelError("");
    setKeyApiKey("");
    setShowKeyPanel(true);
  }, []);

  const closeKeyPanel = useCallback(() => {
    setShowKeyPanel(false);
    setKeyApiKey("");
    setKeyPanelError("");
  }, []);

  const saveKey = useCallback(async (e) => {
    e.preventDefault();
    if (!keyApiKey.trim() || keySaving) return;
    setKeySaving(true);
    setKeyPanelError("");
    try {
      const res  = await apiPut("/api/visun-ai/key", {
        provider: keyProvider,
        apiKey:   keyApiKey.trim(),
        model:    keyModel.trim(),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        setKeyPanelError(data?.message || "Could not save this key.");
        return;
      }
      await fetchKeyConfig();
      setShowKeyPanel(false);
      setKeyApiKey("");
      setErrorMessage("");
      setErrorCode("");
    } catch {
      setKeyPanelError("Could not reach the server. Try again.");
    } finally {
      setKeySaving(false);
    }
  }, [keyApiKey, keyProvider, keyModel, keySaving, fetchKeyConfig]);

  const removeKey = useCallback(async () => {
    if (keyDeleting) return;
    setKeyDeleting(true);
    try {
      await apiDelete("/api/visun-ai/key");
      await fetchKeyConfig();
    } finally {
      setKeyDeleting(false);
    }
  }, [keyDeleting, fetchKeyConfig]);

  // ── Credit balance fetch ─────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res  = await apiGet("/api/ai-credits/balance");
        const data = await res.json().catch(() => null);
        if (!cancelled && data?.success && data.hasLedger) {
          const usd = Number(data.creditsUsd ?? 0);
          setCreditsUsd(usd);
          setHasNoCredits(usd < 0.000001);
          setIsLowCredits(usd < 0.005 && usd >= 0.000001);
        }
      } catch { /* informational only */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Rate limit countdown ─────────────────────────────────────────────────────
  useEffect(() => {
    if (rateLimitSecs <= 0) return;
    const t = setTimeout(() => setRateLimitSecs((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [rateLimitSecs]);

  // ── Loading message rotation ─────────────────────────────────────────────────
  useEffect(() => {
    if (!isSending) { setLoadingMsgIdx(0); return; }
    const id = setInterval(
      () => setLoadingMsgIdx((i) => (i + 1) % LOADING_MESSAGES.length),
      2200
    );
    return () => clearInterval(id);
  }, [isSending]);

  // ── Scroll to bottom ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (msgListRef.current) {
      msgListRef.current.scrollTop = msgListRef.current.scrollHeight;
    }
  }, [messages, isSending]);

  // ── Online / offline ─────────────────────────────────────────────────────────
  useEffect(() => {
    const on  = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online",  on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online",  on);
      window.removeEventListener("offline", off);
    };
  }, []);

  // ── Auto-resize textarea ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!inputRef.current) return;
    inputRef.current.style.height = "auto";
    inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 180)}px`;
  }, [draft]);

  // ── Derived ──────────────────────────────────────────────────────────────────
  const remainingChars    = MAX_PROMPT_LENGTH - draft.length;
  const isOverLimit       = remainingChars < 0;
  const hasMessages       = messages.length > 0;
  const hasUserMessages   = useMemo(() => messages.some((m) => m.role === "user"), [messages]);
  const latestUserMessage = useMemo(
    () => [...messages].reverse().find((m) => m.role === "user"),
    [messages]
  );
  const loadingMessage    = LOADING_MESSAGES[loadingMsgIdx];
  const errCfg            = ERROR_CONFIG[errorCode] || null;
  const ErrIcon           = errCfg?.icon || AlertCircle;
  const errColor          = errCfg?.color || "danger";
  const isKeyError        = errorCode === "ai_key_required" || errorCode === "ai_key_invalid";
  // Admins can fall back to the platform key server-side, so only block sending
  // proactively for non-admin tenants once we know for sure no key is configured.
  const keyBlocksSend     = role !== "admin" && keyConfig !== null && !keyConfig.configured;
  const canSend           = !isSending && isOnline && !isOverLimit && !hasNoCredits && !keyBlocksSend
    && draft.trim().length > 0 && rateLimitSecs <= 0;

  // ── Actions ──────────────────────────────────────────────────────────────────
  const resetConversation = useCallback(() => {
    setMessages([]);
    setDraft("");
    setErrorMessage("");
    setErrorCode("");
    inputRef.current?.focus();
  }, []);

  const copyToClipboard = useCallback(async (messageId, content) => {
    const text = String(content || "").trim();
    if (!text) return;
    try {
      if (navigator?.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.cssText = "position:fixed;opacity:0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopiedId(messageId);
      setTimeout(() => setCopiedId((c) => (c === messageId ? "" : c)), 1500);
    } catch { /* ignore */ }
  }, []);

  const sendMessage = useCallback(async (rawValue, options = {}) => {
    const { isRetry = false } = options;
    const value = String(rawValue || "").trim();
    if (!value || isSending) return;
    if (!isOnline) { setErrorMessage("You are offline. Reconnect and try again."); return; }
    if (value.length > MAX_PROMPT_LENGTH) {
      setErrorMessage(`Message too long. Keep it under ${MAX_PROMPT_LENGTH} characters.`);
      return;
    }

    const userMessage = newMessage("user", value);
    if (!isRetry) setMessages((prev) => [...prev, userMessage]);
    setDraft("");
    setErrorMessage("");
    setErrorCode("");
    setIsSending(true);

    try {
      const historySource = isRetry ? messages : [...messages, userMessage];
      const history = historySource
        .slice(-8)
        .map((m) => ({ role: m.role, content: m.content }));

      const response = await apiPost("/api/visun-ai/chat", {
        message:  value,
        history,
        tenantId: sessionStorage.getItem("tenantId") || undefined,
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok || !payload?.success) {
        const code = payload?.error || "unknown";
        setErrorCode(code);
        if (code === "rate_limit_minute") {
          setRateLimitSecs(payload?.retryAfterSeconds || 60);
        }
        if (code === "insufficient_credits" && payload?.creditsUsd !== undefined) {
          const usd = Number(payload.creditsUsd);
          setCreditsUsd(usd);
          setHasNoCredits(usd < 0.000001);
        }
        if (code === "ai_key_required" || code === "ai_key_invalid") {
          void fetchKeyConfig();
        }
        throw new Error(payload?.message || "VisuN AI request failed.");
      }

      // Update credit balance from settled response
      if (payload.credits?.creditsUsd !== undefined) {
        const usd = Number(payload.credits.creditsUsd);
        setCreditsUsd(usd);
        setHasNoCredits(usd < 0.000001);
        setIsLowCredits(usd < 0.005 && usd >= 0.000001);
      }

      let answer =
        typeof payload.answer === "string" && payload.answer.trim()
          ? payload.answer
          : "I need a bit more context to give you a useful answer. Could you clarify what you're looking for?";

      if (payload.fallbackReason) {
        const r = payload.fallbackReason;
        const status = r.statusCode ? ` ${r.statusCode}` : "";
        answer += `\n\n⚠ AI provider error (${r.provider}${status}): ${r.message}`;
      }

      setMessages((prev) => [
        ...prev,
        newMessage("assistant", answer, { fromApi: true }),
      ]);
    } catch (err) {
      setErrorMessage(err?.message || "VisuN AI is temporarily unavailable. Please try again.");
    } finally {
      setIsSending(false);
    }
  }, [isSending, isOnline, messages, fetchKeyConfig]);

  const handleSend        = useCallback(() => sendMessage(draft), [draft, sendMessage]);
  const handleSubmit      = useCallback((e) => { e.preventDefault(); void handleSend(); }, [handleSend]);
  const handleKeyDown     = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void handleSend(); }
  }, [handleSend]);
  const handleRetry       = useCallback(() => {
    if (!latestUserMessage?.content || isSending) return;
    void sendMessage(latestUserMessage.content, { isRetry: true });
  }, [latestUserMessage, isSending, sendMessage]);
  const handleQuickPrompt = useCallback((prompt) => {
    if (isSending || hasNoCredits) return;
    void sendMessage(prompt);
  }, [isSending, hasNoCredits, sendMessage]);

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <section className="visun-ai-page">
      <div className="va-shell">

        {/* ── HEADER ──────────────────────────────────────────────────────────── */}
        <header className="va-header">
          <div className="va-header-brand">
            <div className="va-header-icon" aria-hidden="true">
              <Bot size={18} />
            </div>
            <div>
              <h1 className="va-header-title">VisuN AI Assistant</h1>
              <p className="va-header-sub">Productivity Insights &amp; Smart Actions</p>
            </div>
          </div>

          <div className="va-header-end">
            {/* Credit balance badge (if tenant has ledger) */}
            {creditsUsd !== null && (
              <span
                className={`va-credit-badge${hasNoCredits ? " empty" : isLowCredits ? " low" : ""}`}
                title="AI credit balance"
              >
                <Wallet size={11} />
                {hasNoCredits ? "No credits" : `$${creditsUsd.toFixed(4)}`}
              </span>
            )}

            <button
              type="button"
              className={`va-key-btn${keyConfig?.configured ? " configured" : ""}`}
              onClick={openKeyPanel}
              title={keyConfig?.configured ? `Using your ${keyConfig.providerLabel} key` : "Add your AI provider key"}
            >
              <Key size={14} />
              {keyConfig?.configured ? keyConfig.providerLabel : "Add AI key"}
            </button>

            <button type="button" className="va-new-btn" onClick={resetConversation}>
              <Plus size={14} />
              New
            </button>
          </div>
        </header>

        {/* ── CHAT ────────────────────────────────────────────────────────────── */}
        <main className="va-chat">

          {/* Messages */}
          <div className="va-messages" ref={msgListRef}>

            {/* Empty state */}
            {!hasMessages && (
              <div className="va-empty">
                <div className="va-empty-icon" aria-hidden="true">
                  <Sparkles size={28} />
                </div>
                <h2>Ask anything about your team&apos;s productivity.</h2>
                <p>Get insights, not just answers.</p>
              </div>
            )}

            {/* Message list */}
            {messages.map((msg) => (
              <div key={msg.id} className={`va-msg va-msg--${msg.role}`}>
                {msg.role === "assistant" && (
                  <div className="va-msg-avatar" aria-hidden="true">
                    <Bot size={14} />
                  </div>
                )}
                <div className="va-msg-body">
                  <div className="va-msg-bubble">
                    {msg.role === "assistant" ? (
                      <div className="va-markdown">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml>
                          {String(msg.content || "")}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <p className="va-user-text">{msg.content}</p>
                    )}
                  </div>

                  {/* Assistant actions — only for API responses */}
                  {msg.role === "assistant" && msg.fromApi && (
                    <div className="va-msg-actions">
                      <button
                        type="button"
                        className="va-action-btn"
                        onClick={() => copyToClipboard(msg.id, msg.content)}
                      >
                        {copiedId === msg.id ? <Check size={11} /> : <Copy size={11} />}
                        {copiedId === msg.id ? "Copied" : "Copy"}
                      </button>

                      {detectActions(msg.content).includes("dashboard") && (
                        <button type="button" className="va-action-btn va-action-btn--primary">
                          <ExternalLink size={11} />
                          View Dashboard
                        </button>
                      )}
                      {detectActions(msg.content).includes("report") && (
                        <button type="button" className="va-action-btn va-action-btn--primary">
                          <Download size={11} />
                          Download Report
                        </button>
                      )}
                      {detectActions(msg.content).includes("investigate") && (
                        <button type="button" className="va-action-btn va-action-btn--warn">
                          <Search size={11} />
                          Investigate User
                        </button>
                      )}
                    </div>
                  )}

                  {/* User timestamp */}
                  {msg.role === "user" && (
                    <span className="va-msg-time">{formatTime(msg.timestamp)}</span>
                  )}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {isSending && (
              <div className="va-msg va-msg--assistant">
                <div className="va-msg-avatar" aria-hidden="true">
                  <Bot size={14} />
                </div>
                <div className="va-msg-body">
                  <div className="va-msg-bubble va-msg-bubble--loading">
                    <span className="va-dot" />
                    <span className="va-dot" />
                    <span className="va-dot" />
                    <span className="va-loading-text">{loadingMessage}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── ERROR BANNER ──────────────────────────────────────────────────── */}
          {errorMessage && (
            <div className={`va-error va-error--${errColor}`} role="alert">
              <ErrIcon size={15} />
              <div className="va-error-body">
                {errCfg?.title && (
                  <span className="va-error-title">{errCfg.title}</span>
                )}
                <span className="va-error-msg">{errorMessage}</span>
              </div>
              {errorCode === "rate_limit_minute" && rateLimitSecs > 0 && (
                <span className="va-countdown">{rateLimitSecs}s</span>
              )}
              {isKeyError && canManageKey && (
                <button type="button" className="va-error-retry" onClick={openKeyPanel}>
                  {errorCode === "ai_key_invalid" ? "Update key" : "Add key"}
                </button>
              )}
              {latestUserMessage?.content && !isKeyError
                && !["insufficient_credits", "no_ledger", "blocked"].includes(errorCode) && (
                <button type="button" className="va-error-retry" onClick={handleRetry}>
                  Retry
                </button>
              )}
            </div>
          )}

          {/* ── LOW CREDIT WARNING ────────────────────────────────────────────── */}
          {isLowCredits && !hasNoCredits && !errorMessage && (
            <div className="va-low-credit">
              <AlertTriangle size={13} />
              <span>
                Credit balance low — <strong>${creditsUsd.toFixed(4)}</strong> remaining.
                Contact your administrator to add credits.
              </span>
            </div>
          )}

          {/* ── NO AI KEY WARNING ─────────────────────────────────────────────── */}
          {keyBlocksSend && !errorMessage && (
            <div className="va-low-credit">
              <KeyRound size={13} />
              <span>
                {canManageKey
                  ? "Add your AI provider API key to start chatting."
                  : "No AI key configured for your organization — ask your administrator to add one."}
              </span>
              {canManageKey && (
                <button type="button" className="va-error-retry" onClick={openKeyPanel}>
                  Add key
                </button>
              )}
            </div>
          )}

          {/* ── COMPOSER ──────────────────────────────────────────────────────── */}
          <div className={`va-composer${hasNoCredits || keyBlocksSend ? " va-composer--blocked" : ""}`}>

            {/* Quick prompt chips — shown only when chat is empty */}
            {!hasUserMessages && (
              <div className="va-prompts" role="list" aria-label="Quick prompts">
                {QUICK_PROMPTS.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    role="listitem"
                    className="va-prompt-chip"
                    onClick={() => handleQuickPrompt(p.prompt)}
                    disabled={isSending || hasNoCredits}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            )}

            <form className="va-input-form" onSubmit={handleSubmit}>
              <div className={`va-input-shell${hasNoCredits ? " va-input-shell--blocked" : ""}`}>
                <textarea
                  ref={inputRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="va-input"
                  placeholder={
                    hasNoCredits
                      ? "No AI credits remaining — contact your administrator"
                      : keyBlocksSend
                      ? canManageKey
                        ? "Add your AI key above to start chatting"
                        : "Ask your administrator to add an AI key"
                      : rateLimitSecs > 0
                      ? `Rate limited — try again in ${rateLimitSecs}s`
                      : "Ask about productivity, reports, or team activity..."
                  }
                  rows={1}
                  disabled={isSending || hasNoCredits || keyBlocksSend}
                  aria-label="VisuN AI prompt"
                />
                <div className="va-input-end">
                  <button
                    type="submit"
                    className="va-send"
                    disabled={!canSend}
                    aria-label="Send message"
                  >
                    <SendHorizontal size={16} />
                  </button>
                </div>
              </div>

              <div className="va-composer-footer">
                <button
                  type="button"
                  className="va-regen"
                  onClick={handleRetry}
                  disabled={!latestUserMessage?.content || isSending || !isOnline}
                >
                  <RotateCcw size={12} />
                  Regenerate
                </button>
                {!isOnline && (
                  <span className="va-offline-badge">
                    <WifiOff size={12} />
                    Offline
                  </span>
                )}
              </div>
            </form>
          </div>
        </main>
      </div>

      {/* ── AI KEY PANEL ───────────────────────────────────────────────────────── */}
      {showKeyPanel && (
        <div className="va-modal-overlay" onClick={closeKeyPanel}>
          <div className="va-modal" onClick={(e) => e.stopPropagation()}>
            <div className="va-modal-header">
              <h3>
                <KeyRound size={16} />
                {keyConfig?.configured ? "Update your AI key" : "Add your AI key"}
              </h3>
              <button type="button" className="va-modal-close" onClick={closeKeyPanel} aria-label="Close">
                <X size={16} />
              </button>
            </div>

            <p className="va-modal-sub">
              Visun AI uses your organization&apos;s own API key — bring the key from any
              supported provider and we&apos;ll use it for every AI request in this tenant.
            </p>

            {canManageKey ? (
              <form onSubmit={saveKey} className="va-key-form">
                <label className="va-key-label">
                  Provider
                  <select
                    value={keyProvider}
                    onChange={(e) => setKeyProvider(e.target.value)}
                    className="va-key-select"
                    disabled={keySaving}
                  >
                    {(keyConfig?.supportedProviders || [
                      { id: "gemini", label: "Google Gemini" },
                      { id: "deepseek", label: "DeepSeek" },
                      { id: "openai", label: "OpenAI" },
                    ]).map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                </label>

                <label className="va-key-label">
                  API key
                  <div className="va-key-input-shell">
                    <input
                      type={showKeyValue ? "text" : "password"}
                      value={keyApiKey}
                      onChange={(e) => setKeyApiKey(e.target.value)}
                      placeholder={
                        keyConfig?.configured ? `Currently: ${keyConfig.maskedApiKey}` : "Paste your provider API key"
                      }
                      className="va-key-input"
                      disabled={keySaving}
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      className="va-key-toggle-visibility"
                      onClick={() => setShowKeyValue((v) => !v)}
                      aria-label={showKeyValue ? "Hide key" : "Show key"}
                    >
                      {showKeyValue ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </label>

                <label className="va-key-label">
                  Model <span className="va-key-optional">(optional)</span>
                  <input
                    type="text"
                    value={keyModel}
                    onChange={(e) => setKeyModel(e.target.value)}
                    placeholder="Defaults to the provider's recommended model"
                    className="va-key-input"
                    disabled={keySaving}
                  />
                </label>

                {keyPanelError && <div className="va-key-error">{keyPanelError}</div>}

                <div className="va-key-actions">
                  {keyConfig?.configured && (
                    <button
                      type="button"
                      className="va-key-remove"
                      onClick={removeKey}
                      disabled={keyDeleting || keySaving}
                    >
                      <Trash2 size={13} />
                      {keyDeleting ? "Removing..." : "Remove key"}
                    </button>
                  )}
                  <button type="submit" className="va-key-save" disabled={keySaving || !keyApiKey.trim()}>
                    {keySaving ? "Saving..." : "Save key"}
                  </button>
                </div>
              </form>
            ) : (
              <p className="va-key-no-permission">
                Only an administrator can add or change the AI key for your organization.
                {keyConfig?.configured
                  ? ` Currently using ${keyConfig.providerLabel}.`
                  : " Ask your administrator to add one."}
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
